import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Icon } from "@/components/Icon";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useOpeners } from "../ui/MobileShell";
import { useMobilePrefs } from "../ui/MobilePrefs";
import { useSheet, SheetBody, SheetTitle } from "../ui/SheetHost";
import FilterSheet from "../sheets/FilterSheet";
import LaunchSheet from "../sheets/LaunchSheet";
import CasinoHero from "./CasinoHero";
import { nativeGamePath } from "@/casino/nativeGames";
import DailyRewardCard from "../ui/DailyRewardCard";
import { ProviderGameHost } from "@/components/casino/ProviderCasinoShelf";
import { isLiveDealer } from "@/lib/casinoClassification";
import { useLiveDealerStatus } from "@/hooks/useLiveDealerStatus";

/* MobileCasino — a vertical GRID per section (not horizontal scrollers). One grid per
   category / provider: Originals (native is_original), Live Dealer (native tables +
   provider live-dealer games), and one section PER PROVIDER (Kerma, JackTop, any future
   one) from /casino/games filtered to provider!=="pnr" && available && !is_live_dealer,
   grouped by provider (mirrors ProviderCasinoShelf's filter/group). Launch logic is
   reused from ProviderCasinoShelf: provider games POST /casino/providers/<p>/launch then
   render the fullscreen <ProviderGameHost> (session ends + wallet refresh on close);
   native games open the LaunchSheet preview → deep-link to the working game flow.
   Duplicate taps are guarded. The lobby chips + illustrated doors FILTER which sections
   show (Lobby = all; Originals = only Originals; Live = only Live Dealer; Slots / Table =
   provider sections). Change 3: Live Dealer nav (?tab=live / ?cat=live, the door, the
   chip) lands on the live-dealer grid, not the full lobby. Each section caps at 12 games
   with a "View all" full-screen grid sheet. REAL catalog; honest empty/error states;
   currency separation (provider games use coins/cash per playCurrency, never bonus). */


// Pretty provider label for the "<Provider> Live" badge on provider live-dealer tiles
// (mirrors desktop pages/Casino.jsx providerLabel; data-driven, never a raw "Live" string).
const PROVIDER_LABEL = { jacktop: "JackTop", kerma: "Kerma", mock: "Sandbox" };
const providerLabel = (p) => PROVIDER_LABEL[p] || (p ? p.charAt(0).toUpperCase() + p.slice(1) : "Provider");
const SECTION_CAP = 12; // cap each section; overflow goes behind "View all"

export default function MobileCasino() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { openSearch } = useOpeners();
  const { openSheet } = useSheet();
  const { playCurrency } = useMobilePrefs();
  const { refresh } = useAuth() || {};
  const [games, setGames] = useState([]);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ prov: [], types: [], sort: "recent" });
  // Change 3: the "Live Dealer" nav (?tab=live from the home live banner + menu, and the
  // ?cat=live category door) lands directly on the combined Live Dealer grid. Any other
  // ?cat=<id> likewise focuses that category door's section. Defaults to the full lobby.
  const initialCat = (() => {
    if (sp.get("tab") === "live") return "live";
    const c = sp.get("cat");
    return ["originals", "slots", "live", "table"].includes(c) ? c : "lobby";
  })();
  const [cat, setCat] = useState(initialCat); // lobby category chips (2a)
  // Provider live-dealer launch (JackTop etc.) — same fullscreen ProviderGameHost the
  // provider grids use. Native live tables keep their existing preview→deep-link flow.
  const [provLaunch, setProvLaunch] = useState(null);
  const [provLaunching, setProvLaunching] = useState(false); // dedupe-launch guard

  // 2c launch preview for a NATIVE game: preview → choose wallet → Play deep-links
  // to the existing working game flow. (Provider games launch seamlessly via the
  // provider shelf's own ProviderGameHost — no preview needed there.)
  const previewLaunch = (g) =>
    openSheet(({ close }) => (
      <LaunchSheet close={close} game={g} onPlay={() => navigate(nativeGamePath(g.slug || g.id))} />
    ), { detent: "medium", label: g.name });

  // 2b provider/type filter sheet.
  const openFilter = () =>
    openSheet(({ close }) => (
      <FilterSheet close={close} providers={[...new Set(games.filter((g) => !g.original && !g.live).map((g) => g.provider).filter(Boolean))]} initial={filters} onApply={setFilters} />
    ), { detent: "large", label: "Filter games" });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/casino/games");
        if (!alive) return;
        setGames(normGames(data));
      } catch { if (alive) setErr(true); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Breakout: a dedicated Live Dealer tile that opens the shared live-blackjack host with the
  // breakout table preselected. Only shows when the server marks breakout visible (intake-gated).
  const [breakoutVisible, setBreakoutVisible] = useState(false);
  useEffect(() => {
    api.get("/casino/bj/tables")
      .then(({ data }) => setBreakoutVisible((data || []).some((t) => t.table_id === "breakout" && t.visible)))
      .catch(() => setBreakoutVisible(false));
  }, []);
  // Real "dealer broadcasting now" status so LIVE badges never claim live while our internal
  // dealers are offline. Provider live-dealer games are external/always-on and keep their badge.
  const liveStatus = useLiveDealerStatus();
  const liveNowFor = (g) => {
    if (g.provider_game) return !!g.live;
    const key = `${g.id || ""} ${g.slug || ""} ${g.name || ""}`.toLowerCase();
    if (g._table === "breakout" || key.includes("breakout")) return !!liveStatus.tableLive?.breakout;
    if (key.includes("baccarat")) return !!liveStatus.baccaratLive;
    if (key.includes("blackjack") || g.id === "liveblackjack" || g.slug === "liveblackjack") return !!liveStatus.bjLive;
    return false;
  };
  const breakoutTile = { id: "breakout", slug: "liveblackjack", name: "Breakout Blackjack",
    img: "/game-art/bj-table-breakout.jpeg", live: true, _table: "breakout" };

  // Change 3: keep the active category in sync with the URL so the Live Dealer nav
  // (?tab=live / ?cat=live) lands on — and stays on — the combined Live Dealer grid even
  // when the screen is already mounted (React Router reuses it; useState won't re-fire).
  useEffect(() => {
    if (sp.get("tab") === "live") { setCat("live"); return; }
    const c = sp.get("cat");
    if (["originals", "slots", "live", "table"].includes(c)) setCat(c);
  }, [sp]);

  const originals = games.filter((g) => g.original);
  // Live Dealer section = native live tables + EVERY provider live-dealer game
  // (JackTop has live-dealer games, not just slots). This mirrors desktop
  // pages/Casino.jsx, which merges native live tables with provider is_live_dealer
  // games into the Live Dealers shelf, and ProviderCasinoShelf, which EXCLUDES
  // is_live_dealer games from the per-provider grids so they surface here instead.
  const nativeLive = games.filter((g) => g.live && !g.provider_game);
  const providerLive = useMemo(
    () => games.filter((g) => g.provider_game && g.available && isLiveDealer(g)),
    [games]
  );
  const live = [...nativeLive, ...(breakoutVisible ? [breakoutTile] : []), ...providerLive]
    .map((g) => ({ ...g, liveNow: liveNowFor(g) }));
  // Change 2: one grid section PER PROVIDER — provider games that are NOT live-dealer
  // (those surface in the Live Dealer section above), grouped by provider. Mirrors how
  // ProviderCasinoShelf filters (provider!=="pnr" && available && !is_live_dealer) + groups.
  const providerGroups = useMemo(() => {
    const map = new Map();
    games
      .filter((g) => g.provider_game && g.available && !isLiveDealer(g))
      .forEach((g) => { const l = map.get(g.provider) || []; l.push(g); map.set(g.provider, l); });
    return Array.from(map.entries());
  }, [games]);

  // Launch any PROVIDER game (live-dealer or slots/table) into the shared fullscreen
  // ProviderGameHost — reuses ProviderCasinoShelf's launch logic (mirrors desktop
  // launchProvider). Native games use previewLaunch instead. Dedupe-guarded.
  const launchProvider = async (g) => {
    if (provLaunching) return;
    if (playCurrency === "bonus") {
      toast.error("Switch to PCoins or PCash to play provider games.");
      return;
    }
    const wallet = playCurrency === "coins" ? "coins" : "cash";
    const wallets = g.wallets || [];
    if (wallets.length && !wallets.includes(wallet)) {
      toast.error(`${g.name} isn't available for ${wallet === "coins" ? "PCoins" : "PCash"}.`);
      return;
    }
    // id is "provider:externalId" — split on ":" when external_game_id isn't carried through.
    const externalId = g.external_game_id || String(g.id || "").split(":").slice(1).join(":") || g.id;
    setProvLaunching(true);
    try {
      const { data } = await api.post(`/casino/providers/${g.provider}/launch`, {
        game_id: externalId,
        wallet,
        locale: navigator.language || "en",
      });
      setProvLaunch({ ...data, wallet, gameName: g.name });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setProvLaunching(false);
    }
  };

  // One handler for a Live Dealer tile: provider live-dealer games launch via
  // ProviderGameHost; native tables deep-link through the existing preview flow.
  const onLivePlay = (g) => (g._table ? navigate(`/casino/play/liveblackjack?table=${g._table}`) : g.provider_game ? launchProvider(g) : previewLaunch(g));
  // A Featured tile can be any game — route to the right launcher.
  const onPlayAny = (g) => (g.provider_game ? launchProvider(g) : previewLaunch(g));

  // Featured spotlight — backend-tagged marquee games ordered by featured_rank
  // (mirrors desktop ProviderCasinoShelf variant="featured"). Lobby-only spotlight.
  const featuredList = useMemo(
    () => games.filter((g) => g.featured).sort((a, b) => (a.featured_rank ?? 999) - (b.featured_rank ?? 999)),
    [games]
  );

  // Change A: the top rotating hero pool — a curated MIX (not only Originals):
  // Featured celeb titles FIRST (ranked), then a few PRYMEBET Originals, then a
  // couple of top provider games (from the per-provider groups). Deduped by id,
  // only games with art, capped at 6 slides. Rebuilt whenever the catalog loads.
  const heroPool = useMemo(() => {
    const topProviderGames = providerGroups.flatMap(([, list]) => list.slice(0, 1));
    const pool = [...featuredList, ...originals.slice(0, 3), ...topProviderGames.slice(0, 2)];
    const seen = new Set();
    return pool.filter((g) => g && g.img && !seen.has(g.id) && seen.add(g.id)).slice(0, 6);
  }, [featuredList, originals, providerGroups]);

  // "View all" — a full-screen grid sheet of ALL a section's games, same tiles + handlers.
  const openViewAll = (title, list, onPlay) =>
    openSheet(({ close }) => (
      <SheetBody style={{ padding: 0 }}>
        <SheetTitle>{title}</SheetTitle>
        <div style={{ padding: "0 16px 16px" }}>
          <div className="m-game-grid">
            {list.map((g) => <GameTile key={g.id} g={g} onPlay={() => { onPlay(g); close(); }} />)}
          </div>
        </div>
      </SheetBody>
    ), { detent: "full", label: title });

  // Apply the 2b filter selection to which shelves show (type-based; no game-type
  // data is fabricated — an empty selection shows everything).
  const hasFilters = filters.prov.length > 0 || filters.types.length > 0;
  const typeSel = new Set(filters.types);
  // The lobby chips (cat) + the 2b type filter both gate which shelves show.
  const catOk = (k) => cat === "lobby" || cat === k;
  const showOriginals = catOk("originals") && (!typeSel.size || typeSel.has("Slots") || typeSel.has("Instant") || typeSel.has("Table games"));
  const showLive = catOk("live") && (!typeSel.size || typeSel.has("Live dealer") || typeSel.has("Table games"));
  const showProviders = (catOk("slots") || catOk("table")) && (!typeSel.size || typeSel.has("Slots"));

  const LOBBY_CHIPS = [["lobby", "Lobby", "casino"], ["originals", "Originals", "originals"], ["slots", "Slots", "slots"], ["live", "Live", "livedealer"], ["table", "Table", "blackjack"]];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, paddingTop: 14 }}>
      {/* Change A: top rotating hero spotlight — a MIX of games (celeb Featured first,
          then Originals, then top provider games). Lobby-only, above the chips row. */}
      {cat === "lobby" && heroPool.length > 0 && (
        <CasinoHero games={heroPool} onPlay={onPlayAny} />
      )}

      {/* Daily reward — the SAME shared claim container as Home (DailyRewardCard), so the
          two stay aligned at all times: claimable → Claim; claimed → live countdown that
          replaces the button. Lobby-only, directly under the rotator for desktop parity. */}
      {cat === "lobby" && <DailyRewardCard />}

      {/* lobby category chips (2a) */}
      <div className="m-no-scrollbar" style={{ display: "flex", gap: 8, padding: "0 16px", overflowX: "auto" }}>
        {LOBBY_CHIPS.map(([id, label, icon]) => {
          const on = cat === id;
          return (
            <button key={id} type="button" onClick={() => setCat(id)} aria-pressed={on}
              style={{ flex: "none", height: 36, padding: "0 14px", borderRadius: 12, display: "flex", alignItems: "center", gap: 7, border: 0, cursor: "pointer",
                font: `${on ? 700 : 600} 13px 'Outfit'`, background: on ? "#F4F4F5" : "#18181C", color: on ? "#0A0A0A" : "#D4D4D8", boxShadow: on ? "none" : "inset 0 0 0 1px #26262B" }}>
              <Icon name={icon} size={15} style={{ color: on ? "#0A0A0A" : "#a1a1aa" }} /> {label}
            </button>
          );
        })}
      </div>

      {/* search as a button + filter (2b) */}
      <div style={{ display: "flex", gap: 10, margin: "0 16px" }}>
        <button
          type="button" onClick={() => openSearch?.()}
          style={{ flex: 1, height: 46, borderRadius: 14, display: "flex", alignItems: "center", gap: 10, padding: "0 14px", textAlign: "left", cursor: "pointer", border: 0,
            background: "#0E0E11", boxShadow: "inset 0 0 0 1px #26262B,inset 0 1px 2px rgba(0,0,0,.45)", color: "#71717a", font: "500 15px 'Outfit'" }}
        >
          <Icon name="search" size={18} style={{ color: "#71717a" }} /> Search games or providers
        </button>
        <button
          type="button" onClick={openFilter} aria-label="Filter games"
          style={{ width: 46, height: 46, flex: "none", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: 0,
            background: hasFilters ? "#F4F4F5" : "#18181C", boxShadow: hasFilters ? "none" : "inset 0 0 0 1px #26262B" }}
        >
          <Icon name="sliders" size={20} style={{ color: hasFilters ? "#0A0A0A" : "#D4D4D8" }} />
        </button>
      </div>

      {/* (Category doors removed on mobile — redundant with the chips row above the
          search, which is the single category selector.) */}

      {loading && <GridSkeleton title="PRYMEBET Originals" />}

      {/* Featured spotlight — backend-tagged marquee games, ordered by rank. Lobby-only
          (hidden under a specific category chip), same as the desktop lobby. */}
      {cat === "lobby" && featuredList.length > 0 && (
        <GridSection title="Featured" icon="star" count={featuredList.length} games={featuredList}
          onPlay={onPlayAny} onViewAll={() => openViewAll("Featured", featuredList, onPlayAny)} />
      )}

      {/* Change 2: one vertical GRID per section (not horizontal scrollers). */}
      {/* PRYMEBET Originals grid */}
      {showOriginals && originals.length > 0 && (
        <GridSection title="PRYMEBET Originals" count={originals.length} games={originals}
          onPlay={previewLaunch} onViewAll={() => openViewAll("PRYMEBET Originals", originals, previewLaunch)} />
      )}

      {/* Live Dealer grid = native live tables + provider live-dealer games (JackTop etc.),
          merged (mirrors desktop). Native tables deep-link via preview; provider live-dealer
          games launch into ProviderGameHost. */}
      {showLive && live.length > 0 && (
        <GridSection title="Live Dealer" count={live.length} games={live}
          onPlay={onLivePlay} onViewAll={() => openViewAll("Live Dealer", live, onLivePlay)} />
      )}

      {/* One grid section PER PROVIDER (Kerma / JackTop / any future provider). Launches
          into the fullscreen ProviderGameHost in the active play currency. */}
      {showProviders && providerGroups.map(([provider, list]) => (
        <GridSection key={provider} title={providerLabel(provider)} count={list.length} games={list}
          onPlay={launchProvider} onViewAll={() => openViewAll(providerLabel(provider), list, launchProvider)} />
      ))}

      {err && games.length === 0 && (
        <p style={{ textAlign: "center", color: "#71717a", font: "500 13px 'Outfit'", padding: "20px 16px" }}>Couldn’t load the lobby. Try again.</p>
      )}

      {/* Fullscreen host for a launched provider game (same host the desktop provider
          shelves use). Ends the provider session + refreshes the wallet on close. */}
      <ProviderGameHost
        launch={provLaunch}
        onClose={async () => {
          const closing = provLaunch;
          setProvLaunch(null);
          if (closing?.provider && closing?.session_id) {
            try { await api.post(`/casino/providers/${closing.provider}/sessions/${closing.session_id}/end`); } catch { /* UI still closes */ }
          }
          if (refresh) { try { await refresh(); } catch { /* non-fatal */ } }
        }}
      />
    </div>
  );
}

/* A vertical grid section: title + count, optional "View all" (right of the title when
   the section exceeds SECTION_CAP), then a responsive grid (2 cols on small phones, 3 on
   larger) capped at SECTION_CAP tiles. */
function GridSection({ title, icon, count, games, onPlay, onViewAll }) {
  const shown = games.slice(0, SECTION_CAP);
  const hasMore = games.length > SECTION_CAP;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {icon && <Icon name={icon} size={18} style={{ color: "#00D632" }} />}
          <span style={{ font: "800 17px 'Manrope'", letterSpacing: "-.02em" }}>{title}</span>
          <span className="m-tnum" style={{ font: "600 12px 'Outfit'", color: "#71717a" }}>{count}</span>
        </span>
        {hasMore && (
          <button type="button" onClick={onViewAll} aria-label={`View all ${title}`} style={{ display: "flex", alignItems: "center", gap: 2, padding: "8px 0 8px 8px", font: "600 13px 'Outfit'", color: "#a1a1aa", background: "none", border: 0, cursor: "pointer" }}>
            View all<Icon name="chevron-right" size={14} style={{ color: "#a1a1aa" }} />
          </button>
        )}
      </div>
      <div style={{ padding: "0 16px" }}>
        <div className="m-game-grid">
          {shown.map((g) => <GameTile key={g.id} g={g} onPlay={() => onPlay(g)} />)}
        </div>
      </div>
    </div>
  );
}

/* Square-ish art tile + name below (grid cell). */
function GameTile({ g, onPlay }) {
  return (
    <button type="button" onClick={onPlay} style={{ display: "flex", flexDirection: "column", gap: 7, background: "none", border: 0, cursor: "pointer", padding: 0, textAlign: "left" }}>
      <span style={{ position: "relative", width: "100%", aspectRatio: "1 / 1", borderRadius: 16, overflow: "hidden", background: "#141417", boxShadow: "0 10px 24px rgba(0,0,0,.4)" }}>
        {g.img
          ? <img src={g.img} alt={g.name} loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          : <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="casino" size={28} style={{ color: "#3F3F46" }} /></span>}
        <span style={{ position: "absolute", inset: 0, borderRadius: "inherit", background: "linear-gradient(180deg,rgba(255,255,255,.12) 0%,rgba(255,255,255,0) 28%)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.08)" }} />
        {g.liveNow && <span style={{ position: "absolute", left: 8, top: 8, font: "800 9px 'Outfit'", letterSpacing: ".12em", padding: "2px 6px", borderRadius: 999, background: "rgba(239,68,68,.9)", color: "#fff" }}>LIVE</span>}
        {/* Provider tiles carry a studio badge (e.g. "JackTop") so the player sees the source. */}
        {g.provider_game && g.providerLabel && (
          <span style={{ position: "absolute", right: 8, top: 8, font: "800 8px 'Outfit'", letterSpacing: ".08em", padding: "2px 6px", borderRadius: 999, background: "rgba(10,10,10,.78)", color: "#fff", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.14)" }}>{g.providerLabel}</span>
        )}
      </span>
      <span style={{ font: "700 13px 'Outfit'", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>{g.name}</span>
    </button>
  );
}

function GridSkeleton({ title }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ padding: "0 16px", font: "800 17px 'Manrope'", color: "#3F3F46" }}>{title}</div>
      <div style={{ padding: "0 16px" }}>
        <div className="m-game-grid">
          {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} style={{ width: "100%", aspectRatio: "1 / 1", borderRadius: 16, background: "#141417" }} />)}
        </div>
      </div>
    </div>
  );
}

// Native game artwork by slug (native "pnr" games carry NO thumbnail_url — map art from
// the game id/slug, same assets the desktop Home / lobby use, so Originals tiles aren't blank).
const NATIVE_IMG = {
  "dragon-tower": "/game-art/dragon-tower.jpeg",
  keno: "/game-art/keno.jpeg",
  mines: "/game-art/mines.jpeg", dice: "/game-art/dice.jpeg", crash: "/game-art/crash.jpeg",
  blackjack: "/game-art/blackjack.jpeg", slots: "/game-art/slots.jpeg", roulette: "/game-art/roulette.jpeg",
  coinflip: "/game-art/coinflip.jpeg", baccarat: "/game-art/baccarat.jpeg", hilo: "/game-art/hilo.jpeg", plinko: "/game-art/plinko.jpeg",
  liveblackjack: "/game-art/live_dealer.jpeg", live: "/game-art/live_dealer.jpeg",
};

function normGames(data) {
  const arr = Array.isArray(data) ? data : data?.games || data?.items || [];
  return arr.map((g) => {
    // A provider game is anything NOT served by our native "pnr" engine. Provider live-dealer
    // games (e.g. JackTop) carry is_live_dealer + external_game_id and launch via ProviderGameHost.
    const providerGame = Boolean(g.provider && g.provider !== "pnr" && g.provider !== "pnrbet");
    const slug = g.id || g.slug || g.key;
    const cat = String(g.category || "").toLowerCase();
    // Live dealer = the backend's normalized flag, OR a native game in the "live" category.
    const liveDealer = Boolean(g.is_live_dealer) || (!providerGame && cat === "live");
    // Native games have NO thumbnail_url — map art from the slug.
    const img = g.thumbnail_url || g.img || g.image || (!providerGame ? NATIVE_IMG[String(slug).toLowerCase()] : null);
    return {
      id: g.id || g.external_game_id || g.slug || g.key || g.name,
      slug: !providerGame ? slug : undefined,
      name: g.name || g.title || "Game",
      img,
      provider: g.provider,
      provider_game: providerGame,
      providerLabel: providerGame ? providerLabel(g.provider) : null,
      external_game_id: g.external_game_id,
      wallets: g.wallets || null,
      available: g.available !== false,
      is_live_dealer: liveDealer,
      // Featured spotlight (backend-tagged marquee games, ordered by featured_rank).
      featured: Boolean(g.featured),
      featured_rank: g.featured_rank,
      // Originals = native non-live games (backend category "original"/"table"/"slots").
      original: !providerGame && !liveDealer,
      live: liveDealer,
    };
  }).filter((g) => g.name);
}

